import React, { useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useCarTutorial } from '@/api/cars/use-tutorial';
import useCheckinStore from '@/stores/use-checkin';
import { Button } from '@/ui';
import { Loader } from '@/ui/loader';

import AuthorizedDrivers from './authorized-drivers';
import BatteryCheck from './battery-check';
import DepositPolicy from './deposit-policy';
import DynamicInstruction from './dynamic-instruction';
import DynamicQuestion from './dynamic-question';
import ImportantInfo from './important-info';
import NoSmoking from './no-smoking';
import UsedTeslaPrompt from './used-tesla-prompt';

const questions = [
  {
    question:
      'Which of the following are violations of our rules and will cost you your deposit?',
    options: [
      {
        id: 1,
        value: 'Running the battery down to 0',
      },
      {
        id: 2,
        value: 'Driving more than 300 miles in a day',
      },
      {
        id: 3,
        value: 'Using autopilot functions',
      },
      {
        id: 4,
        value: 'Smoking of any kind in the car',
      },
      {
        id: 5,
        value: 'Allowing an unauthorised person to drive',
      },
    ],
    correctAnswers: [1, 4, 5],
  },
];

interface TutorialProps {
  car: Car;
  onComplete: () => void;
}

export default function Tutorial({ car, onComplete }: TutorialProps) {
  const { styles, theme } = useStyles(stylesheet);
  const step = useCheckinStore((state) => state.step);
  const setStep = useCheckinStore((state) => state.setStep);

  const { data: steps, isPending: isFetchingSteps } = useCarTutorial({
    //@ts-ignore
    variables: { id: car.id },
    enabled: !!car.id,
  });

  const tutorialSteps = useMemo(() => {
    if (!steps) return [];

    if (car.brandName === 'Rivian') {
      return [
        {
          element: <BatteryCheck />,
          showButtons: true,
        },
        {
          element: <NoSmoking />,
          showButtons: true,
        },
        {
          element: <AuthorizedDrivers />,
          showButtons: true,
        },
        {
          element: <DepositPolicy />,
          showButtons: true,
        },
        {
          element: <ImportantInfo />,
          showButtons: true,
        },
        ...questions.map((question, idx) => ({
          element: (
            <DynamicQuestion
              key={`question-${idx}`}
              question={question}
              onContinue={() => setStep(step + 1)}
              onGoBack={() => setStep(step - 1)}
            />
          ),
          showButtons: false,
        })),
      ];
    }

    return [
      {
        element: (
          <UsedTeslaPrompt
            onClick={(isTeslaPro: boolean) => {
              if (isTeslaPro) {
                setStep(steps.instructions.length + 1);
              } else {
                setStep(step + 1);
              }
            }}
          />
        ),
        showButtons: false,
      },
      ...steps.instructions.map((instruction) => ({
        element: (
          <DynamicInstruction key={instruction.id} instruction={instruction} />
        ),
        showButtons: true,
      })),
      {
        element: <BatteryCheck />,
        showButtons: true,
      },
      {
        element: <NoSmoking />,
        showButtons: true,
      },
      {
        element: <AuthorizedDrivers />,
        showButtons: true,
      },
      {
        element: <DepositPolicy />,
        showButtons: true,
      },
      {
        element: <ImportantInfo />,
        showButtons: true,
      },
      ...questions.map((question, idx) => ({
        element: (
          <DynamicQuestion
            key={`question-${idx}`}
            question={question}
            onContinue={() => setStep(step + 1)}
            onGoBack={() => setStep(step - 1)}
          />
        ),
        showButtons: false,
      })),
    ];
  }, [steps, step]);

  useEffect(() => {
    if (tutorialSteps.length > 0 && step === tutorialSteps.length) {
      setStep(0);
      onComplete();
    }
  }, [tutorialSteps.length, step]);

  if (isFetchingSteps) {
    return <Loader />;
  }

  if (tutorialSteps.length === 0) return null;

  if (!tutorialSteps[step]?.element) return null;

  return (
    <View style={{ flexGrow: 1 }}>
      <View style={styles.progressBarContainer}>
        <View
          style={{
            height: 4,
            width: `${(step / tutorialSteps.length) * 100}%`,
            backgroundColor: theme.colors.progressBar.active.backgroundColor,
          }}
        />
      </View>

      <View style={styles.stepContainer}>
        {step === tutorialSteps.length - 1 ? (
          <View style={{ flexGrow: 1 }}>{tutorialSteps[step].element}</View>
        ) : (
          <View>{tutorialSteps[step].element}</View>
        )}

        {tutorialSteps[step].showButtons ? (
          <View>
            <Button
              text="Next"
              type="primary"
              onPress={() => {
                setStep(step + 1);
              }}
            />

            {step > 0 && (
              <Button
                text="Go Back"
                type="ghost"
                onPress={() => {
                  setStep(step - 1);
                }}
              />
            )}
          </View>
        ) : null}
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  progressBarContainer: {
    width: '100%',
    height: 4,
    paddingLeft: {
      md: 16,
    },
  },
  stepContainer: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_1,
    justifyContent: 'space-between',
    padding: 16,
  },
}));
